import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "@/motion/appMotion";
import { X } from "lucide-react";

/**
 * Generic, reusable modal primitive.
 *
 * Handles the concerns every modal shares — a React portal, backdrop,
 * Escape-to-close, body scroll lock, focus container and enter/exit
 * animation — while leaving the content entirely up to the caller.
 *
 * Compose feature-specific modals on top of this (see PartnerApplicationModal).
 *
 * Props:
 *  - isOpen            : boolean — controls visibility
 *  - onClose           : () => void — called on backdrop click, Escape and the X button
 *  - children          : modal body
 *  - maxWidthClassName : Tailwind max-w-* for the panel (default "max-w-lg")
 *  - panelClassName    : extra classes merged onto the panel
 *  - showClose         : render the floating X button (default true)
 *  - closeOnBackdrop   : close when the backdrop is clicked (default true)
 *  - ariaLabel         : accessible label for the dialog
 */
const Modal = ({
  isOpen,
  onClose,
  children,
  maxWidthClassName = "max-w-lg",
  panelClassName = "",
  showClose = true,
  closeOnBackdrop = true,
  ariaLabel,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 overscroll-contain">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={closeOnBackdrop ? onClose : undefined}
            aria-hidden
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.28, type: "spring", bounce: 0.18 }}
            className={`relative z-10 flex w-full ${maxWidthClassName} max-h-[min(92dvh,92vh)] min-h-0 flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl ${panelClassName}`}
            onClick={(event) => event.stopPropagation()}
          >
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30"
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            )}
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default Modal;
